import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import MapLayers from './MapLayers';
import MapLayerToolbar from './MapLayerToolbar';
import './WeatherMap.css';

const WeatherMap = forwardRef(({ isActive, zipcode = '59911' }, ref) => {
  const mapContainerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapInstanceRef = useRef(null);
  const [timestamp, setTimestamp] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAnimation, setIsLoadingAnimation] = useState(false); // New state for animation loading
  const [sliderValue, setSliderValue] = useState(0);
  const [radarFrames, setRadarFrames] = useState([]);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const animationIntervalRef = useRef(null);
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const [activeLayer, setActiveLayer] = useState('radar');
  const [layerOpacity, setLayerOpacity] = useState(0.75); // Default opacity of 75%
  const baseLayerRef = useRef(null);
  const labelOverlayRef = useRef(null);
  const overlayLayersRef = useRef({});
  const weatherLayerRef = useRef(null);
  const [activeBaseLayer, setActiveBaseLayer] = useState('base_satellite');
  const [radarData, setRadarData] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [radarError, setRadarError] = useState(null);
  const [currentFrameTime, setCurrentFrameTime] = useState('');
  const [currentZipcode, setCurrentZipcode] = useState(zipcode);
  const frameLayersRef = useRef([]);
  // Add loading state ref at component level
  const isLoadingRef = useRef(false);
  // Add loading state and request tracking refs
  const retryTimerRef = useRef(null);
  const currentRequestIdRef = useRef(0);
  // Track the current product for layer changes
  const [currentProduct, setCurrentProduct] = useState(null);
  const animationStartTimeoutRef = useRef(null); // Ref for the initial start delay timeout
  const previousZoomRef = useRef(null); // Ref to store previous zoom level
  const zoomDelayTimeoutRef = useRef(null); // Ref for zoom delay timeout
  
  // Refs to track current state for useCallback stability
  const isPlayingRef = useRef(isPlaying);
  const isLoadingAnimationRef = useRef(isLoadingAnimation);

  // Update refs whenever state changes
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { isLoadingAnimationRef.current = isLoadingAnimation; }, [isLoadingAnimation]);
  
  // When zipcode prop changes, update the currentZipcode state
  useEffect(() => {
    const lastZipcode = localStorage.getItem('last_weather_zipcode');
    
    // Only update if zipcode has actually changed
    if (zipcode !== currentZipcode && zipcode !== lastZipcode) {
      console.log(`Zipcode changed from ${currentZipcode} to ${zipcode}`);
      setCurrentZipcode(zipcode);
      localStorage.setItem('last_weather_zipcode', zipcode);
    }
  }, [zipcode, currentZipcode]);
  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    // Methods that parent can call
    refreshMap: () => {
      if (mapInstanceRef.current) {
        console.log('Refreshing map size');
        mapInstanceRef.current.invalidateSize();
      }
    },
    reloadRadarData: () => {
      loadRadarData();
    },
    updateBaseLayer,
    addOverlay,
    removeOverlay,
    startRadarAnimation,
    stopRadarAnimation,
    refreshRadar: loadRadarData,
    goToFrame: (index) => {
      if (radarData && index >= 0 && index < radarData.frames.length) {
        setCurrentFrameIndex(index);
        updateRadarFrame(index);
      }
    }
  }));
  
  // Initialize map when component mounts or tab becomes active
  useEffect(() => {
    // Check if Leaflet is loaded and the map container exists
    const leafletExists = typeof window !== 'undefined' && window.L;
    const containerExists = mapContainerRef.current !== null;
    
    console.log('Map component useEffect triggered');
    console.log('Map tab active:', isActive);
    console.log('Map container exists:', containerExists);
    console.log('Leaflet exists:', leafletExists);
    
    if (isActive && containerExists) {
      console.log('Tab is active, initializing map');
      initializeMap();
    }
    
    // Clean up on unmount
    return () => {
      if (animationIntervalRef.current) {
        console.log('Component cleanup: clearing animation');
        window.cancelAnimationFrame(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [isActive]);
  
  // Helper function to get coordinates for a zipcode
  const getCoordinatesForZipcode = async (zipcode, forceLookup = false) => {
    try {
      console.log(`Getting coordinates for zipcode: ${zipcode}, forceLookup: ${forceLookup}`);
      
      // Try to load from cache first
      const cachedCoords = localStorage.getItem(`weather_coords_${zipcode}`);
      if (cachedCoords && !forceLookup) {
        try {
          const coords = JSON.parse(cachedCoords);
          // Basic validation of cached coords
          if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
             console.log(`Using cached coordinates for ${zipcode}: ${coords.lat}, ${coords.lng}`);
             return coords;
          } else {
             console.warn(`Invalid cached coordinates found for ${zipcode}, removing.`);
             localStorage.removeItem(`weather_coords_${zipcode}`);
          }
        } catch (parseError) {
           console.warn(`Error parsing cached coordinates for ${zipcode}, removing.`, parseError);
           localStorage.removeItem(`weather_coords_${zipcode}`);
        }
      }
      
      // If forcing lookup or not in cache, get from API
      console.log(`Fetching coordinates for ${zipcode} from API...`);
      // Use the /current endpoint as it includes location data
      const apiUrl = `/wp-json/oht-weather/v1/current?zipcode=${zipcode}`;
      const response = await fetch(apiUrl, {
         method: 'GET',
         headers: {
           'Accept': 'application/json'
         }
      });

      if (!response.ok) {
         throw new Error(`Failed to fetch current weather for coordinates: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.location && typeof data.location.latitude === 'number' && typeof data.location.longitude === 'number') {
         const coords = {
           lat: data.location.latitude,
           lng: data.location.longitude
         };
         console.log(`Fetched coordinates for ${zipcode}: ${coords.lat}, ${coords.lng}`);
         // Cache the newly fetched coordinates
         localStorage.setItem(`weather_coords_${zipcode}`, JSON.stringify(coords));
         return coords;
      } else {
         console.error(`API response for ${zipcode} did not contain valid location coordinates.`);
         return null; // Return null if coordinates aren't found
      }

    } catch (error) {
      console.error(`Error getting coordinates for ${zipcode}:`, error);
      return null;
    }
  };
  
  // Single effect to handle radar loading and map initialization
  useEffect(() => {
    let isSubscribed = true;
    let retryTimer = null;

    const initAndLoad = async () => {
      if (!isActive || !mapInstanceRef.current || isLoadingRef.current) {
        return;
      }

      try {
        // First initialize map if needed
        if (!mapLoaded) {
          console.log('Initializing map...');
          initializeMap();
          setMapLoaded(true);
        }

        // Then load radar data if we don't have it yet
        if (radarFrames.length === 0) {
          console.log('Loading initial radar data');
          isLoadingRef.current = true;

          await loadAnimationData('radarFcst');

          // Only retry if we still need frames
          if (isSubscribed && radarFrames.length === 0) {
            console.log('No radar frames loaded, will retry once');
            retryTimer = setTimeout(() => {
              if (isSubscribed && radarFrames.length === 0) {
                console.log('Retrying radar data load');
                loadAnimationData('radarFcst');
              }
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error in initialization or loading:', error);
      } finally {
        if (!retryTimer) {
          isLoadingRef.current = false;
        }
      }
    };

    initAndLoad();

    return () => {
      isSubscribed = false;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      isLoadingRef.current = false;
    };
  }, [isActive]);
  
  // Initialize the map
  const initializeMap = () => {
    console.log('Initializing map...');
    // Check if Leaflet is available
    if (!window.L) {
      console.warn('Leaflet library not loaded yet');
      
      // Try to load Leaflet from CDN if not available
      if (!document.querySelector('script[src*="leaflet"]')) {
        console.log('Loading Leaflet from CDN');
        loadLeaflet();
      }
      return;
    }
    
    // Check if map container exists
    if (!mapContainerRef.current) {
      console.warn('Map container element not found');
      setTimeout(() => {
        if (mapContainerRef.current) {
          console.log('Map container found after delay, initializing map');
          initializeMap();
        }
      }, 500);
      return;
    }
    
    // Check if map is already initialized
    if (mapInstanceRef.current) {
      console.log('Map already initialized, resizing');
      mapInstanceRef.current.invalidateSize(true);
      return;
    }
    
    // Helper function to get coordinates for a zipcode
    const getCoordinatesForZipcode = async (zipcode) => {
        try {
            console.log(`Getting coordinates for zipcode: ${zipcode}`);
            // Try to load from cache first
            const cachedCoords = localStorage.getItem(`weather_coords_${zipcode}`);
            if (cachedCoords) {
                const coords = JSON.parse(cachedCoords);
                console.log(`Using cached coordinates for ${zipcode}: ${coords.lat}, ${coords.lng}`);
                return coords;
            }
            
            // If coordinates are already set in the map, use those
            if (mapInstanceRef.current) {
                const center = mapInstanceRef.current.getCenter();
                if (center) {
                    const currentCoords = {
                        lat: center.lat,
                        lng: center.lng
                    };
                    // Cache these coordinates
                    localStorage.setItem(`weather_coords_${zipcode}`, JSON.stringify(currentCoords));
                    console.log(`Using current map coordinates for ${zipcode}: ${currentCoords.lat}, ${currentCoords.lng}`);
                    return currentCoords;
                }
            }
            
            // Only make API request if we really need to
            console.log(`No cached coordinates for ${zipcode}, will get from radar data`);
            return null;
        } catch (error) {
            console.error('Error getting coordinates:', error);
            return null;
        }
    };
    
    try {
      console.log('Creating new Leaflet map instance');
      const L = window.L;
      
      // Force map resize
      if (mapContainerRef.current.clientWidth === 0 || mapContainerRef.current.clientHeight === 0) {
        console.warn('Map container has zero width or height, delaying initialization');
        setTimeout(initializeMap, 500);
        return;
      }
      
      // Get coordinates based on the current zipcode
      console.log(`Initializing map for zipcode: ${currentZipcode}`);
      
      // Default coordinates (Bigfork, MT) as fallback
      let defaultLat = 48.053;
      let defaultLng = -114.082;
      
      // Try to get coordinates from localStorage
      const cachedCoords = localStorage.getItem(`weather_coords_${currentZipcode}`);
      if (cachedCoords) {
        try {
          const coords = JSON.parse(cachedCoords);
          if (coords && coords.lat && coords.lng) {
            console.log(`Using cached coordinates for ${currentZipcode}: ${coords.lat}, ${coords.lng}`);
            defaultLat = coords.lat;
            defaultLng = coords.lng;
          }
        } catch (error) {
          console.error('Error parsing cached coordinates:', error);
        }
      }
      
      // Create the map instance
      const map = L.map(mapContainerRef.current, {
        center: [defaultLat, defaultLng],
        zoom: 8,
        zoomControl: false, // We'll add zoom control separately
        attributionControl: true
      });
      
      // Base satellite imagery
      const esriSat = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles, Roads, Labels © Esri & contributors',
        maxZoom: 19
      }).addTo(map);
      
      // Transparent overlay: city & place labels
      const esriLabels = L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        zIndex: 15 // Ensure labels are above base map and weather layer
      }).addTo(map);
      
      // Transparent overlay: roads, highways, and streets
      const esriRoads = L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        zIndex: 20 // Ensure roads are above labels and weather layer
      }).addTo(map);
      
      baseLayerRef.current = esriSat;
      labelOverlayRef.current = esriLabels;
      
      // Store roads layer in overlayLayersRef for later use
      overlayLayersRef.current['overlay_esri_roads'] = esriRoads;
      overlayLayersRef.current['overlay_esri_labels'] = esriLabels;
      
      // Set earth as the base map layer type
      setActiveLayer('radar');
      
      // Add zoom control with proper position
      L.control.zoom({
        position: 'topleft'
      }).addTo(map);
      
      // Store the map instance in ref
      mapInstanceRef.current = map;
      
      // Set default timestamp
      const now = new Date();
      const currentTime = now.toLocaleTimeString('en-US', {
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
      setTimestamp(currentTime);
      
      setMapLoaded(true);
      console.log('Map initialized successfully');
      
      // If we used default coordinates, also try to get the actual coordinates
      // and update the map center once we have them
      if (!cachedCoords) {
        getCoordinatesForZipcode(currentZipcode).then(coords => {
          if (coords && mapInstanceRef.current) {
            console.log(`Updating map center to: ${coords.lat}, ${coords.lng}`);
            mapInstanceRef.current.setView([coords.lat, coords.lng], 8);
          }
        });
      }

      // --- Initialize zoom listener ---
      // Set initial zoom level
      previousZoomRef.current = map.getZoom();
      console.log('Attaching zoomend listener, initial zoom:', previousZoomRef.current);
      map.on('zoomend', handleZoomEnd);
      // No need for a separate useEffect for the listener, as initializeMap should only run once per map instance
      // Cleanup will be handled by the component unmount effect or if the map instance changes (less likely)

    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };
  
  // Load Leaflet from CDN if not available
  const loadLeaflet = () => {
    // Load CSS
    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = 'https://unpkg.com/leaflet@1.9.3/dist/leaflet.css';
    document.head.appendChild(linkElement);
    
    // Load JS
    const scriptElement = document.createElement('script');
    scriptElement.src = 'https://unpkg.com/leaflet@1.9.3/dist/leaflet.js';
    scriptElement.onload = initializeMap;
    document.body.appendChild(scriptElement);
    console.log('Leaflet script added to document');
  };
  
  // Load radar data from API
  const loadRadarData = async () => {
    console.log(`Loading radar data for current active layer with zipcode: ${currentZipcode}`);
    
    // Map activeLayer to product parameter
    let productParam = 'radarFcst'; // default
    
    switch(activeLayer) {
      case 'radar':
        // Request the forecast radar product to get future frames
        productParam = 'radarFcst'; 
        break;
      case 'radar_satellite':
        productParam = 'satradFcst';
        break;
      case 'satellite':
        productParam = 'cloudsFcst';
        break;
      case 'precipitation':
        productParam = 'precip24hr';
        break;
      case 'snow':
        productParam = 'snow24hr';
        break;
      case 'temp':
        productParam = 'tempFcst';
        break;
      case 'temp_feels_like':
        productParam = 'feelsLikeFcst';
        break;
      case 'dew_point':
        productParam = 'dewpointFcst';
        break;
      case 'wind_speed_gusts':
        productParam = 'windSpeedFcst';
        break;
      case 'uv_index':
        productParam = 'uvFcst';
        break;
      default:
        productParam = 'radarFcst';
    }
    
    // Skip if we already have frames for this product
    if (radarFrames.length > 0 && radarData && radarData.product === productParam) {
      console.log(`Already have frames for ${productParam}, skipping load`);
      return;
    }
    
    // Use the shared animation loading function
    loadAnimationData(productParam);
  };
  
  // Load animation data for a specific product
  const loadAnimationData = async (productParam) => {
    // check if product changed
    
    // Skip if already loading or if we already have frames
    if ( isLoadingRef.current || radarFrames.length > 0) {
      console.log('Already loading or have frames, skipping load');
      return;
    }

    console.log(`Loading animation data for product: ${productParam} with zipcode: ${currentZipcode}`);
    isLoadingRef.current = true;
    
    try {
      const apiUrl = `/wp-json/oht-weather/v1/radar?zipcode=${currentZipcode}&product=${productParam}`;
      console.log(`Fetching animation data from: ${apiUrl}`);
      
      const wpNonce = window?.wpApiSettings?.nonce || '';
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };
      
      if (wpNonce) {
        headers['X-WP-Nonce'] = wpNonce;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Failed with status: ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('Animation data response:', responseData);
      
      if (responseData && responseData.radar) {
        setRadarData(responseData.radar);
        
        if (responseData.radar.frames && responseData.radar.frames.length > 0) {
          console.log(`Got ${responseData.radar.frames.length} animation frames`);
          
          // Process the frames to ensure they have timeLabel
          const processedFrames = responseData.radar.frames.map(frame => {
            return {
              ...frame,
              timeLabel: frame.timeLabel || frame.timestamp || ''
            };
          });
          
          // Update the animation frames
          setRadarFrames(processedFrames);
          
          // Show the first frame (index 0) by default instead of the last frame
          setCurrentFrameIndex(0);
          setSliderValue(0); // Set slider to the start position (0%)
          
          // Display the first frame
          if (processedFrames.length > 0) {
            const firstFrame = processedFrames[0];
            displayRadarFrame(firstFrame, 0);
          }
        } else {
          console.warn('No animation frames in response');
          setRadarFrames([]);
          setRadarError('No radar data available');
        }
      } else {
        console.error('Invalid radar data in response');
        setRadarError('Invalid radar data received');
      }
    } catch (error) {
      console.error('Error loading animation data:', error);
      setRadarError('Failed to load radar data');
    } finally {
      isLoadingRef.current = false;
    }
  };
  
  // Add a helper function to consistently display radar frames
  const displayRadarFrame = (frame, frameIndex) => {
    if (!frame || !frame.url || !mapInstanceRef.current || !window.L) {
      console.error('Cannot display radar frame - missing required elements');
      return;
    }
    
    console.log(`Displaying radar frame ${frameIndex}`);
    
    // If we have frame layers, just update their opacity
    if (frameLayersRef.current.length > 0) {
      frameLayersRef.current.forEach((layer, index) => {
        layer.setOpacity(index === frameIndex ? layerOpacity : 0);
      });
      setCurrentFrameTime(frame.timeLabel || '');
      return;
    }
    
    // Otherwise create a single layer
    try {
      // Remove any existing weather layer
      if (weatherLayerRef.current) {
        mapInstanceRef.current.removeLayer(weatherLayerRef.current);
        weatherLayerRef.current = null;
      }
      
      // Create and add new layer with current opacity
      weatherLayerRef.current = window.L.tileLayer(frame.url, {
        opacity: layerOpacity,
        zIndex: 10,
        minZoom: 3,
        maxZoom: 11,
        maxNativeZoom: 11
      }).addTo(mapInstanceRef.current);
      
      setCurrentFrameTime(frame.timeLabel || '');
    } catch (error) {
      console.error('Failed to create or add radar layer:', error);
    }
  };
  
  // Update the updateRadarFrame function to use our new helper
  const updateRadarFrame = (frameIndex) => {
    if (!mapInstanceRef.current || !radarFrames || frameIndex >= radarFrames.length || radarFrames.length === 0) {
      console.warn('Cannot update radar frame: invalid state', {
        mapExists: !!mapInstanceRef.current,
        framesCount: radarFrames?.length || 0,
        requestedIndex: frameIndex
      });
      return;
    }
    
    try {
      const frame = radarFrames[frameIndex];
      if (!frame || !frame.url) {
        console.error('Invalid frame data at index', frameIndex, frame);
        return;
      }
      
      displayRadarFrame(frame, frameIndex); 
    } catch (error) {
      console.error('Error updating radar frame:', error);
    }
  };
  
  
  
  // Update the base map layer
  const updateBaseLayer = (layerId, layerUrl) => {
    if (!mapInstanceRef.current || !window.L) return;
    
    const L = window.L;
    console.log(`Updating base layer to: ${layerId} with URL: ${layerUrl}`);
    
    try {
      // Remove current base layer if it exists
      if (baseLayerRef.current) {
        mapInstanceRef.current.removeLayer(baseLayerRef.current);
        baseLayerRef.current = null;
      }
      
      // Create new base layer
      baseLayerRef.current = L.tileLayer(layerUrl, {
        attribution: layerId.includes('satellite') ? 'Tiles © Esri & contributors' : '© OpenStreetMap contributors',
        minZoom: 4,
        maxZoom: 18,
        zIndex: 1 // Ensure base layer is behind other layers
      });
      
      // Add the new base layer to the map
      baseLayerRef.current.addTo(mapInstanceRef.current);
      
      // When changing the base layer, manage overlay layers appropriately
      if (layerId === 'base_roads') {
        // For roads/OSM base layer, remove Esri labels and roads overlays
        // since OSM already includes roads and labels
        if (labelOverlayRef.current) {
          mapInstanceRef.current.removeLayer(labelOverlayRef.current);
        }
        
        if (overlayLayersRef.current['overlay_esri_roads']) {
          mapInstanceRef.current.removeLayer(overlayLayersRef.current['overlay_esri_roads']);
        }
      } else {
        // For satellite/imagery base layer, ensure both overlays are visible
        if (labelOverlayRef.current && !mapInstanceRef.current.hasLayer(labelOverlayRef.current)) {
          labelOverlayRef.current.addTo(mapInstanceRef.current);
        }
        
        if (overlayLayersRef.current['overlay_esri_roads'] && 
            !mapInstanceRef.current.hasLayer(overlayLayersRef.current['overlay_esri_roads'])) {
          overlayLayersRef.current['overlay_esri_roads'].addTo(mapInstanceRef.current);
        }
      }
      
      // Store the active base layer ID
      setActiveBaseLayer(layerId);
      
    } catch (error) {
      console.error(`Error updating base layer to ${layerId}:`, error);
    }
  };
  
  // Add an overlay layer
  const addOverlay = (layerId, layerUrl) => {
    if (!mapInstanceRef.current || !window.L) return;
    
    const L = window.L;
    console.log(`Adding overlay: ${layerId} with URL: ${layerUrl}`);
    
    try {
      // First remove if it already exists
      if (overlayLayersRef.current[layerId]) {
        mapInstanceRef.current.removeLayer(overlayLayersRef.current[layerId]);
        delete overlayLayersRef.current[layerId];
      }
      
      // Get API key from WordPress settings - define once for use throughout function
      const apiKey = window?.wpApiSettings?.weatherApiKey || '';
      
      // Check if this is a vector-warnings endpoint URL
      if (layerUrl && layerUrl.includes('/vector-warnings')) {
        console.log(`Processing vector-warnings overlay: ${layerId}`);
        
        // Fetch data from our PHP endpoint
        fetch(layerUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(`Error fetching vector warnings: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            if (!data || !data.productKey) {
              console.error('Invalid vector warnings data returned:', data);
              return;
            }
            
            console.log(`Got vector data for ${layerId}, product key: ${data.productKey}`);
            
            // Check if there are any features available
            if (data.features && data.features.features && data.features.features.length === 0) {
              console.log(`No features found for ${layerId} - this is normal if there are no current warnings in this area`);
            }
            
            // Extract product ID from the response
            let productId = data.productKey || '608';
            
            // For winter warnings (610), use a lower fixed zoom level
            let maxZoom = 10;
            let minZoom = 3;
            
            if (productId === '610') {
              // Winter warnings - use very low zoom to avoid too many requests
              console.log('Winter warnings product detected - using lower zoom levels (3-6)');
              maxZoom = 6;
              minZoom = 3;
            } else if (['608', '609', '611', '612', '607'].includes(productId)) {
              // Other weather warnings - use medium-low zoom
              console.log(`Weather warning product ${productId} detected - using lower zoom levels (4-7)`);
              maxZoom = 7;
              minZoom = 4;
            }
            
            // Create the vector overlay
            console.log(`Creating vector overlay for ${layerId}, product: ${productId}`);
            
            // The base URL will be our PHP endpoint without any query params
            const baseEndpointUrl = "/wp-json/oht-weather/v1/vector-warnings";
            
            // Define the VectorTileLayer class
            const VectorTileLayer = L.TileLayer.extend({
              initialize: function(options) {
                L.TileLayer.prototype.initialize.call(this, 'placeholder', options);
                
                // Set up a counter to limit concurrent API requests
                this.pendingRequests = 0;
                this.maxConcurrentRequests = 4; // Max concurrent requests
                this.requestQueue = [];
              },
              
              createTile: function(coords, done) {
                const tile = L.DomUtil.create('img', 'leaflet-tile');
                tile.width = tile.height = this.options.tileSize;
                
                // Keep track of the tile in our loading system
                const tileKey = `${coords.z}:${coords.x}:${coords.y}`;
                
                // Set opacity based on zoom level (more transparent at lower zoom levels)
                if (coords.z < 5) {
                  tile.style.opacity = '0.6'; // More transparent at lower zoom
                } else {
                  tile.style.opacity = '0.8';
                }
                
                // If we've already tried this tile too many times, skip it
                if (this.options.errorTracker && this.options.errorTracker.shouldSkipTile(tileKey)) {
                  console.log(`Skipping tile ${tileKey} due to previous load failures`);
                  setTimeout(() => {
                    done(null, tile);
                  }, 10);
                  return tile;
                }
                
                // Check if we can load a new tile right now or need to queue
                if (this.pendingRequests >= this.maxConcurrentRequests) {
                  // Queue this request
                  this.requestQueue.push({
                    coords: coords,
                    tile: tile,
                    callback: done
                  });
                  return tile;
                }
                
                // Increase pending requests counter
                this.pendingRequests++;
                
                // Load the tile
                this._loadTile(coords, tile, done);
                
                return tile;
              },
              
              _loadTile: function(coords, tile, done) {
                const tileKey = `${coords.z}:${coords.x}:${coords.y}`;
                
                try {
                  // Get the correct URL for this tile
                  const url = this.getTileUrl(coords);
                  
                  // Check if the URL is valid
                  if (!url) {
                    // Empty URL is expected for out-of-range tiles, no need to log as error
                    this._handleTileError(tileKey, tile, done, 'Empty URL', true);
                    return;
                  }
                  
                  // The fetch should be to our WordPress endpoint which will handle caching
                  fetch(url)
                    .then(response => {
                      if (!response.ok) {
                        throw new Error(`Error ${response.status}: ${response.statusText}`);
                      }
                      return response.json();
                    })
                    .then(data => {
                      // Check if there are features in this tile
                      const hasFeatures = data && data.features && 
                                       data.features.features && 
                                       data.features.features.length > 0;
                      
                      if (hasFeatures) {
                        console.log(`Tile ${tileKey} has ${data.features.features.length} features`);
                        
                        // Filter out features that have already been added at other zoom levels
                        // to prevent duplication
                        let filteredFeatures = data.features;
                        if (this.options.addedFeatureIds) {
                          filteredFeatures = {
                            ...data.features,
                            features: data.features.features.filter(feature => {
                              // Use a unique ID for each feature based on its properties
                              const featureId = feature.id || 
                                             (feature.properties && feature.properties.featureId) ||
                                             JSON.stringify(feature.properties);
                              
                              // If we've already added this feature, skip it
                              if (this.options.addedFeatureIds[featureId]) {
                                return false;
                              }
                              
                              // Mark this feature as added
                              this.options.addedFeatureIds[featureId] = true;
                              return true;
                            })
                          };
                        }
                        
                        // Create GeoJSON layer for this tile's features
                        const geoJsonLayer = L.geoJSON(filteredFeatures, {
                          style: function(feature) {
                            if (feature.properties && feature.properties.severityCode) {
                              const severityColors = {
                                '1': { color: '#ff0000', weight: 3, opacity: 0.9, fillOpacity: 0.2 },
                                '2': { color: '#ff6600', weight: 2, opacity: 0.8, fillOpacity: 0.2 },
                                '3': { color: '#ffcc00', weight: 2, opacity: 0.8, fillOpacity: 0.2 },
                                '4': { color: '#00cc00', weight: 2, opacity: 0.7, fillOpacity: 0.2 }
                              };
                              return severityColors[feature.properties.severityCode] || 
                                { color: '#3388ff', weight: 2, opacity: 0.7, fillOpacity: 0.2 };
                            }
                            return { color: '#3388ff', weight: 2, opacity: 0.7, fillOpacity: 0.2 };
                          },
                          onEachFeature: function(feature, layer) {
                            if (feature.properties) {
                              // Add source layerId to each feature for tracking and removal
                              feature.properties.sourceLayerId = this.options.layerId;
                              
                              // Store a custom property on the layer for easier identification
                              layer.sourceLayerId = this.options.layerId;
                              
                              let popupContent = `<strong>${feature.properties.eventDescription || 'Weather Alert'}</strong><br>`;
                              if (feature.properties.headlineText) {
                                popupContent += `${feature.properties.headlineText}<br>`;
                              }
                              // Add timing information if available
                              if (feature.properties.effectiveTimeLocal) {
                                popupContent += `<strong>Effective:</strong> ${feature.properties.effectiveTimeLocal}<br>`;
                              }
                              if (feature.properties.expireTimeLocal) {
                                popupContent += `<strong>Expires:</strong> ${feature.properties.expireTimeLocal}<br>`;
                              }
                              layer.bindPopup(popupContent);
                            }
                          }.bind({ options: { layerId: layerId }})
                        });
                        
                        // Store reference to source overlay
                        geoJsonLayer.sourceLayerId = layerId;
                        
                        // If there's an existing layer, remove it first
                        if (tile.geoJsonLayer && mapInstanceRef.current) {
                          mapInstanceRef.current.removeLayer(tile.geoJsonLayer);
                        }
                        
                        // Store the GeoJSON layer with the tile
                        tile.geoJsonLayer = geoJsonLayer;
                        geoJsonLayer.addTo(mapInstanceRef.current);
                        
                        // Make tile transparent
                        tile.style.opacity = '0';
                      } else {
                        // Empty tile - indicate visually but subtly
                        tile.style.backgroundColor = 'transparent';
                        tile.dataset.hasFeatures = 'false';
                        
                        // If there's an existing GeoJSON layer, remove it
                        if (tile.geoJsonLayer && mapInstanceRef.current) {
                          mapInstanceRef.current.removeLayer(tile.geoJsonLayer);
                          tile.geoJsonLayer = null;
                        }
                      }
                      
                      // Reset error tracking for this tile
                      if (this.options.errorTracker) {
                        this.options.errorTracker.resetError(tileKey);
                      }
                      
                      // Load next queued tile request, if any
                      this._finishTileLoad(tile, null, done);
                    })
                    .catch(error => {
                      console.error(`Error loading tile ${tileKey}:`, error);
                      this._handleTileError(tileKey, tile, done, error);
                    });
                } catch (error) {
                  console.error(`Exception in tile load ${tileKey}:`, error);
                  this._handleTileError(tileKey, tile, done, error);
                }
              },
              
              _finishTileLoad: function(tile, error, callback) {
                // Decrease pending requests counter
                this.pendingRequests--;
                
                // Call the done callback
                if (callback) {
                  callback(error, tile);
                }
                
                // Process next queued request if available
                if (this.requestQueue.length > 0 && this.pendingRequests < this.maxConcurrentRequests) {
                  const nextRequest = this.requestQueue.shift();
                  this.pendingRequests++;
                  this._loadTile(nextRequest.coords, nextRequest.tile, nextRequest.callback);
                }
              },
              
              _handleTileError: function(tileKey, tile, callback, error, isExpected = false) {
                // Only log unexpected errors
                if (!isExpected) {
                  // Track the error
                  if (this.options.errorTracker) {
                    this.options.errorTracker.recordError(tileKey);
                  }
                  
                  // Only log the first error for a tile to avoid console spam
                  if (!this.options.errorTracker || this.options.errorTracker.errors[tileKey].count <= 1) {
                    console.warn(`Tile error for ${tileKey}: ${error}`);
                  }
                }
                
                // Mark the tile as empty
                tile.style.backgroundColor = 'transparent';
                tile.dataset.hasFeatures = 'false';
                tile.dataset.error = 'true';
                
                // Finish loading process
                this._finishTileLoad(tile, error, callback);
              },
              
              _abortLoading: function() {
                // Clean up any GeoJSON layers when removing tiles
                if (this._tiles) {
                  Object.values(this._tiles).forEach(tile => {
                    if (tile.geoJsonLayer && mapInstanceRef.current) {
                      mapInstanceRef.current.removeLayer(tile.geoJsonLayer);
                    }
                  });
                }
                // Call parent implementation
                L.TileLayer.prototype._abortLoading.call(this);
              },
              
              _removeTile: function(key) {
                // Clean up GeoJSON layer if it exists
                if (this._tiles[key] && this._tiles[key].geoJsonLayer && mapInstanceRef.current) {
                  console.debug(`Removing GeoJSON layer for tile key ${key}`);
                  mapInstanceRef.current.removeLayer(this._tiles[key].geoJsonLayer);
                  this._tiles[key].geoJsonLayer = null;
                }
                // Call parent implementation
                L.TileLayer.prototype._removeTile.call(this, key);
              },
              
              onRemove: function(map) {
                // Remove all GeoJSON layers when this layer is removed
                if (this._tiles) {
                  console.debug(`Cleaning up all GeoJSON layers`);
                  Object.keys(this._tiles).forEach(key => {
                    if (this._tiles[key].geoJsonLayer && mapInstanceRef.current) {
                      mapInstanceRef.current.removeLayer(this._tiles[key].geoJsonLayer);
                      this._tiles[key].geoJsonLayer = null;
                    }
                  });
                }
                
                // Find and remove any layers with matching sourceLayerId
                if (this.options && this.options.layerId) {
                  const layerId = this.options.layerId;
                  console.debug(`Searching for orphaned GeoJSON layers for ${layerId}`);
                  
                  const layersToRemove = [];
                  map.eachLayer(function(layer) {
                    if (layer.sourceLayerId === layerId || 
                        (layer.feature && 
                         layer.feature.properties && 
                         layer.feature.properties.sourceLayerId === layerId)) {
                      layersToRemove.push(layer);
                    }
                  });
                  
                  layersToRemove.forEach(layer => {
                    console.debug(`Removing orphaned GeoJSON layer for ${layerId}`);
                    map.removeLayer(layer);
                  });
                }
                
                // Reset feature tracking
                if (this.options && this.options.addedFeatureIds) {
                  this.options.addedFeatureIds = {};
                }
                
                // Call parent implementation
                L.TileLayer.prototype.onRemove.call(this, map);
              }
            });
            
            // Create the vector overlay using our PHP endpoint
            const vectorOverlay = new VectorTileLayer({
              baseUrl: baseEndpointUrl,
              productId: productId,
              zipcode: data.zipcode || currentZipcode,
              timestamp: data.time,
              layerId: layerId,
              minZoom: minZoom,
              maxZoom: maxZoom,
              maxNativeZoom: maxZoom,
              opacity: 0.8,
              zIndex: 80,
              // Keep track of added feature IDs to prevent duplicates
              addedFeatureIds: {},
              errorTracker: {
                errors: {},
                maxRetries: 2,
                retryDelay: 2000,
                
                shouldSkipTile: function(key) {
                  if (!this.errors[key]) return false;
                  // Skip if we've hit max retries or if the tile is marked as empty
                  return this.errors[key].count >= this.maxRetries || this.errors[key].isEmpty;
                },
                
                recordError: function(key) {
                  if (!this.errors[key]) {
                    this.errors[key] = { count: 0, lastAttempt: 0 };
                  }
                  
                  const now = Date.now();
                  this.errors[key].count++;
                  this.errors[key].lastAttempt = now;
                  
                  // If this is an empty tile response, mark it
                  if (this.errors[key].count === 1) {
                    this.errors[key].isEmpty = true;
                  }
                  
                  console.warn(`Tile error for ${key}, retry count: ${this.errors[key].count}`);
                },
                
                shouldRetry: function(key) {
                  if (!this.errors[key]) return true;
                  
                  const now = Date.now();
                  const timeSinceLastAttempt = now - this.errors[key].lastAttempt;
                  
                  if (this.errors[key].count >= this.maxRetries) {
                    return false;
                  }
                  
                  if (timeSinceLastAttempt < this.retryDelay) {
                    return false;
                  }
                  
                  return true;
                },
                
                resetError: function(key) {
                  if (this.errors[key]) {
                    delete this.errors[key];
                  }
                }
              },
              tileSize: 512,
              updateInterval: 5000,
              updateWhenIdle: true,
              keepBuffer: 4
            });
            
            // Update the getTileUrl method to work with our PHP endpoint
            vectorOverlay.getTileUrl = function(coords) {
              try {
                if (!this.options) return '';
                
                const z = coords.z;
                const x = coords.x;
                const y = coords.y;
                
                // Always request features at zoom level 4
                const featureZoom = 4;
                let featureX = x;
                let featureY = y;
                
                // Calculate feature coordinates if we're at a higher zoom level
                if (z > featureZoom) {
                  const zoomDiff = z - featureZoom;
                  featureX = Math.floor(x / Math.pow(2, zoomDiff));
                  featureY = Math.floor(y / Math.pow(2, zoomDiff));
                }
                
                // For winter warnings (610) and other national products, use fixed zoom levels
                if (this.options.productId === '610' && z > 6) {
                  // Skip higher zoom levels for winter warnings
                  return '';
                }
                
                // For all warnings, limit to the appropriate zoom levels
                if (z < this.options.minZoom || z > this.options.maxZoom) {
                  // Skip out of range zoom levels
                  return '';
                }
                
                // For national-level weather products, use precalculated tiles
                // that cover the entire US with as few tiles as possible
                let finalUrl = `${this.options.baseUrl}?product=${this.options.productId}&zipcode=${this.options.zipcode}&x=${x}&y=${y}&zoom=${z}`;
                
                return finalUrl;
              } catch (error) {
                console.error('Error in getTileUrl:', error);
                return '';
              }
            };
            
            // Store reference and add to map
            overlayLayersRef.current[layerId] = vectorOverlay;
            vectorOverlay.addTo(mapInstanceRef.current);
            
            // Handle any GeoJSON data directly from first response
            if (data.features && data.features.type === "FeatureCollection" && data.features.features && data.features.features.length > 0) {
              console.log(`Adding ${data.features.features.length} GeoJSON features directly to map`);
              
              // Process features to ensure tracking properties
              const processedFeatures = {
                ...data.features,
                features: data.features.features.map(feature => {
                  return {
                    ...feature,
                    properties: {
                      ...feature.properties,
                      sourceLayerId: layerId,
                      productKey: data.productKey
                    }
                  };
                })
              };
              
              // Create GeoJSON layer with the features
              const geoJsonLayer = L.geoJSON(processedFeatures, {
                style: function(feature) {
                  // Use different colors based on severity if available
                  if (feature.properties && feature.properties.severityCode) {
                    // Color scheme based on severity (1=extreme, 2=severe, 3=moderate, 4=minor)
                    const severityColors = {
                      '1': { color: '#ff0000', weight: 3, opacity: 0.9, fillOpacity: 0.5 }, // Extreme - Red
                      '2': { color: '#ff6600', weight: 2, opacity: 0.8, fillOpacity: 0.4 }, // Severe - Orange 
                      '3': { color: '#ffcc00', weight: 2, opacity: 0.8, fillOpacity: 0.4 }, // Moderate - Yellow
                      '4': { color: '#00cc00', weight: 2, opacity: 0.7, fillOpacity: 0.3 }  // Minor - Green
                    };
                    return severityColors[feature.properties.severityCode] || 
                      { color: '#3388ff', weight: 2, opacity: 0.7, fillOpacity: 0.4 }; // Default blue
                  }
                  
                  // Default styling
                  return { color: '#3388ff', weight: 2, opacity: 0.7, fillOpacity: 0.4 };
                },
                onEachFeature: function(feature, layer) {
                  // Add source layer ID to the layer object for tracking
                  layer.sourceLayerId = layerId;
                  
                  if (feature.properties) {
                    let popupContent = `<strong>${feature.properties.eventDescription || 'Weather Alert'}</strong><br>`;
                    
                    if (feature.properties.headlineText) {
                      popupContent += `${feature.properties.headlineText}<br>`;
                    }
                    
                    // Add timing information if available
                    if (feature.properties.effectiveTimeLocal) {
                      popupContent += `<strong>Effective:</strong> ${feature.properties.effectiveTimeLocal}<br>`;
                    }
                    if (feature.properties.expireTimeLocal) {
                      popupContent += `<strong>Expires:</strong> ${feature.properties.expireTimeLocal}<br>`;
                    }
                    
                    layer.bindPopup(popupContent);
                  }
                }
              });
              
              // Add source layer ID to the entire GeoJSON layer
              geoJsonLayer.sourceLayerId = layerId;
              
              // Store as a separate overlay
              const geoJsonLayerId = `${layerId}_geojson`;
              overlayLayersRef.current[geoJsonLayerId] = geoJsonLayer;
              geoJsonLayer.addTo(mapInstanceRef.current);
            }
          })
          .catch(error => {
            console.error(`Error processing vector warnings for ${layerId}:`, error);
          });
      }
      
      // Handle vector API overlays (direct API URLs - for backward compatibility)
      if (layerUrl && layerUrl.includes('vector-api')) {
        // Extract product key from URL
        const productKeyMatch = layerUrl.match(/products\/(\d+)/);
        if (!productKeyMatch || !productKeyMatch[1]) {
          console.error('Could not extract product key from URL:', layerUrl);
          return;
        }
        
        const productKey = productKeyMatch[1];
        console.log(`Adding vector overlay for product: ${productKey}`);
        
        // Determine correct subdomain
        const subdomains = ['api', 'api0', 'api1', 'api2', 'api3'];
        const subdomain = subdomains[Math.floor(Math.random() * subdomains.length)];
        
        // Current time in seconds (not milliseconds)
        const currentTime = Math.floor(Date.now() / 1000);
        
        // Create a custom vector tile layer for the Weather.com vector API
        const VectorTileLayer = L.TileLayer.extend({
          initialize: function(options) {
            L.TileLayer.prototype.initialize.call(this, 'placeholder', options);
          },
          getTileUrl: function(coords) {
            try {
              if (!this.options) return '';
              
              // The Weather.com vector API requires different z/x/y calculations
              const z = coords.z;
              const x = coords.x;
              const y = coords.y;
              
              // Get base URL details from options
              const { productKey, subdomain, apiKey, currentTime } = this.options;
              
              // Create proper URL with coordinates
              return `https://${subdomain}.weather.com/v2/vector-api/products/${productKey}/features?time=${currentTime}&x=${x}&y=${y}&lod=${z}&apiKey=${apiKey}&tile-size=512`;
            } catch (error) {
              console.error('Error in VectorTileLayer getTileUrl:', error);
              return '';
            }
          },
          _abortLoading: function() {
            // Override to avoid errors when removing layer
            L.TileLayer.prototype._abortLoading.call(this);
          }
        });
        
        // Create the layer with our custom class
        const vectorOverlay = new VectorTileLayer({
          productKey: productKey,
          subdomain: subdomain,
          apiKey: apiKey,
          currentTime: currentTime,
          opacity: 0.8,
          zIndex: 80
        });
        
        // Store reference and add to map
        overlayLayersRef.current[layerId] = vectorOverlay;
        vectorOverlay.addTo(mapInstanceRef.current);
      } 
      // Handle TileServer overlays
      else if (layerUrl && layerUrl.includes('TileServer')) {
        // For TileServer URLs with xyz format
        const urlParts = layerUrl.split('?');
        const baseUrl = urlParts[0];
        const params = urlParts[1] || '';
        
        console.log(`Creating TileServer overlay for ${layerId}:`, { 
          baseUrl,
          params: params.substring(0, 100) + '...' // Log first 100 chars
        });
        
        const CustomTileLayer = L.TileLayer.extend({
          initialize: function(options) {
            L.TileLayer.prototype.initialize.call(this, 'placeholder', options);
            
            // Set up zoom level constraints
            this.minZoom = options.minZoom || 3;
            this.maxZoom = options.maxZoom || 11;
            this.maxNativeZoom = options.maxNativeZoom || 11;
          },
          
          getTileUrl: function(coords) {
            try {
              if (!this.options) return '';
              
              const z = coords.z;
              const x = coords.x;
              const y = coords.y;
              
              // Check if zoom level is within bounds
              if (z < this.minZoom || z > this.maxZoom) {
                return '';
              }
              
              // For weather layers, we need to adjust the coordinates based on zoom level
              let adjustedX = x;
              let adjustedY = y;
              
              // If we're at a higher zoom level than the native zoom, we need to adjust coordinates
              if (z > this.maxNativeZoom) {
                const zoomDiff = z - this.maxNativeZoom;
                adjustedX = Math.floor(x / Math.pow(2, zoomDiff));
                adjustedY = Math.floor(y / Math.pow(2, zoomDiff));
              }
              
              // Get base URL and params
              let baseUrl = this.options.baseUrl;
              let params = new URLSearchParams(this.options.params);
              
              // Update the xyz parameter with adjusted coordinates
              params.delete('xyz');
              params.set('xyz', `${adjustedX}:${adjustedY}:${z}`);
              
              // For weather layers, ensure we're using the correct timestamp
              if (this.options.timestamp) {
                params.set('ts', this.options.timestamp);
              }
              
              return `${baseUrl}?${params.toString()}`;
            } catch (error) {
              console.error('Error in getTileUrl:', error);
              return '';
            }
          }
        });
        
        const tileLayer = new CustomTileLayer({
          baseUrl: baseUrl,
          params: params,
          minZoom: 3,
          maxZoom: 11,
          maxNativeZoom: 11,
          timestamp: validTimestamp,
          opacity: 0.7,
          zIndex: 100
        });
        
        // Store reference and add to map
        overlayLayersRef.current[layerId] = tileLayer;
        tileLayer.addTo(mapInstanceRef.current);
      }
      // Handle standard tile overlays
      else if (layerUrl) {
        // For standard xyz tile URLs
        overlayLayersRef.current[layerId] = L.tileLayer(layerUrl, {
          opacity: 0.7,
          zIndex: 100
        });
        
        // Add to map
        overlayLayersRef.current[layerId].addTo(mapInstanceRef.current);
      }
      
    } catch (error) {
      console.error(`Error adding overlay ${layerId}:`, error);
    }
  };
  
  // Remove an overlay layer
  const removeOverlay = (layerId) => {
    if (!mapInstanceRef.current) return;
    
    console.log(`Removing overlay: ${layerId}`);
    
    // Remove the main overlay layer
    if (overlayLayersRef.current[layerId]) {
      // For vector layers, we need to clean up GeoJSON layers
      const layer = overlayLayersRef.current[layerId];
      
      // Clean up any GeoJSON layers associated with tiles
      if (layer._tiles) {
        console.debug(`Cleaning up ${Object.keys(layer._tiles).length} tile layers for ${layerId}`);
        Object.values(layer._tiles).forEach(tile => {
          if (tile.geoJsonLayer && mapInstanceRef.current) {
            console.debug(`Removing GeoJSON layer for tile at ${tile.coords.x},${tile.coords.y},${tile.coords.z}`);
            mapInstanceRef.current.removeLayer(tile.geoJsonLayer);
          }
        });
      }
      
      // Remove the main layer
      console.debug(`Removing main layer for ${layerId}`);
      mapInstanceRef.current.removeLayer(layer);
      delete overlayLayersRef.current[layerId];
      
      // Reset any stored feature IDs for this layer to prevent issues on re-adding
      if (layer.options && layer.options.addedFeatureIds) {
        layer.options.addedFeatureIds = {};
      }
    }
    
    // Also remove any associated GeoJSON layer (for initial vector data)
    const geoJsonLayerId = `${layerId}_geojson`;
    if (overlayLayersRef.current[geoJsonLayerId]) {
      console.debug(`Removing associated GeoJSON layer ${geoJsonLayerId}`);
      mapInstanceRef.current.removeLayer(overlayLayersRef.current[geoJsonLayerId]);
      delete overlayLayersRef.current[geoJsonLayerId];
    }
    
    // Find and remove any orphaned GeoJSON layers that might still be on the map
    // This is needed because some GeoJSON layers might not be properly tracked
    if (mapInstanceRef.current && window.L) {
      const layersToRemove = [];
      
      mapInstanceRef.current.eachLayer(function(mapLayer) {
        // Check multiple ways to identify if this layer belongs to our overlay
        let shouldRemove = false;
        
        // 1. Check if it has our sourceLayerId property
        if (mapLayer.sourceLayerId && mapLayer.sourceLayerId === layerId) {
          shouldRemove = true;
        }
        
        // 2. Check for feature with sourceLayerId property
        else if (mapLayer.feature && 
            mapLayer.feature.properties && 
            mapLayer.feature.properties.sourceLayerId === layerId) {
          shouldRemove = true;
        }
        
        // 3. Check for feature properties that might match our layer
        else if (mapLayer.feature && 
            mapLayer.feature.properties && 
            layerId.includes(mapLayer.feature.properties.productKey)) {
          shouldRemove = true;
        }
        
        // 4. Check for GeoJSON layer associated with this overlay
        else if (mapLayer._layers) {
          let hasMatchingFeature = false;
          try {
            // Attempt to inspect child layers for matching properties
            Object.values(mapLayer._layers).forEach(childLayer => {
              if (childLayer.feature && 
                  childLayer.feature.properties && 
                  (childLayer.feature.properties.sourceLayerId === layerId || 
                   childLayer.sourceLayerId === layerId)) {
                hasMatchingFeature = true;
              }
            });
          } catch (e) {
            // Ignore errors when traversing layers
          }
          shouldRemove = hasMatchingFeature;
        }
        
        if (shouldRemove) {
          layersToRemove.push(mapLayer);
        }
      });
      
      // Remove all identified layers
      layersToRemove.forEach(layerToRemove => {
        console.debug(`Removing identified GeoJSON layer for ${layerId}`);
        mapInstanceRef.current.removeLayer(layerToRemove);
      });
    }
  };
  
  // Fetch the Product Inventory Series List 
  const fetchProductInventory = async (productType) => {
    try {
      // Get API key from WordPress settings
      const apiKey = window?.wpApiSettings?.weatherApiKey || '';
      let url = `https://api.weather.com/v3/TileServer/series/productSet/PPAcore?apiKey=${apiKey}`;
      
      if (productType) {
        url += `&filter=${productType}`;
      }
      
      console.log(`Fetching product inventory from: ${url}`);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error fetching product inventory: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Product inventory fetched:', data);
      
      // Process and return the series data
      if (data && data.seriesInfo) {
        return data.seriesInfo;
      } else {
        throw new Error('Invalid product inventory response');
      }
    } catch (error) {
      console.error('Error fetching product inventory:', error);
      return null;
    }
  };
  
  
  // Toggle layer selection panel
  const toggleLayersPanel = () => {
    const newPanelState = !showLayersPanel;
    setShowLayersPanel(newPanelState);
    
    // Ensure the panel is positioned correctly
    if (newPanelState) {
      setTimeout(() => {
        const layersPanel = document.querySelector('.layers-panel');
        if (layersPanel) {
          // Set fixed positioning
          layersPanel.style.right = '2px';
          layersPanel.style.bottom = '2px';
        }
      }, 0);
    }
  };
  
  // Effect to handle layer changes and loading
  useEffect(() => {
    if (currentProduct) {
      loadAnimationData(currentProduct);
    }
  }, [currentProduct]);
  
  // Update opacity for weather layers only
  const updateLayerOpacity = (opacity) => {
    setLayerOpacity(opacity);
    
    // Update all overlay layers (weather overlays)
    Object.values(overlayLayersRef.current).forEach(layer => {
      if (layer) {
        layer.setOpacity(opacity);
      }
    });
    
    // Update weather layer
    if (weatherLayerRef.current) {
      weatherLayerRef.current.setOpacity(opacity);
    }
    
    // Update frame layers if they exist (for animations)
    frameLayersRef.current.forEach(layer => {
      if (layer) {
        layer.setOpacity(opacity);
      }
    });
  };

  // Update handleLayerSelect to handle opacity changes
  const handleLayerSelect = (layerId, layerUrl) => {
    console.log(`Layer selected: ${layerId}, URL: ${layerUrl}`);
    
    // Handle opacity changes
    if (layerId === 'opacity') {
      updateLayerOpacity(layerUrl);
      return;
    }
    
    // Preserve current map view before changing layers
    if (mapInstanceRef.current) {
      const currentZoom = mapInstanceRef.current.getZoom();
      const currentCenter = mapInstanceRef.current.getCenter();
      mapInstanceRef.current.userZoom = currentZoom;
      mapInstanceRef.current.userCenter = currentCenter;
    }

    // Check if this is a base layer
    if (layerId.startsWith('base_')) {
      console.log(`Switching base layer to ${layerId}`);
      updateBaseLayer(layerId, layerUrl);
      return;
    }

    // Check if this is an overlay
    if (layerId.startsWith('overlay_')) {
      console.log(`Toggling overlay: ${layerId}`);
      if (layerUrl) {
        // Add overlay
        addOverlay(layerId, layerUrl);
      } else {
        // Remove overlay
        removeOverlay(layerId);
      }
      return;
    }

    // Otherwise, it's a weather layer (radar, satellite, etc.)
    console.log(`Switching weather layer to ${layerId}`);
    
    // Stop animation and reset state before loading new layer
    setIsPlaying(false); // <-- Explicitly set isPlaying to false here
    stopRadarAnimation(); // This will now clear both timeouts
    setCurrentFrameIndex(0);
    setSliderValue(0);
    setActiveLayer(layerId);
    isLoadingRef.current = false;
    setRadarFrames([]);
    setRadarData(null);
    
    // Remove existing weather layer if any
    if (weatherLayerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(weatherLayerRef.current);
      weatherLayerRef.current = null;
    }

    // Map layerId to product parameter
    let productParam = 'radarFcst'; // default
    
    switch(layerId) {
      case 'radar':
        productParam = 'radarFcst'; 
        break;
      case 'radar_satellite':
        productParam = 'satradFcst';
        break;
      case 'satellite':
        productParam = 'cloudsFcst';
        break;
      case 'precipitation':
        productParam = 'precip24hr';
        break;
      case 'snow':
        productParam = 'snow24hr';
        break;
      case 'temp':
        productParam = 'tempFcst';
        break;
      case 'temp_feels_like':
        productParam = 'feelsLikeFcst';
        break;
      case 'dew_point':
        productParam = 'dewpointFcst';
        break;
      case 'wind_speed_gusts':
        productParam = 'windSpeedFcst';
        break;
      case 'uv_index':
        productParam = 'uvFcst';
        break;
      default:
        productParam = 'radarFcst';
    }
    
    // Set the new product - loading will be handled by the effect
    setCurrentProduct(productParam);
  };
  
  // Toggle locations
  const toggleLocations = () => {
    console.log('Toggle locations');
    // Implementation would depend on your location data
  };
  
  // Toggle play/pause
  const togglePlayPause = () => {
    console.log(`${isPlaying ? 'Pausing' : 'Playing'} radar animation`);
    setIsPlaying(!isPlaying);
  };
  
  // Go to previous frame
  const goPrevFrame = () => {
    if (currentFrameIndex > 0) {
      const newIndex = currentFrameIndex - 1;
      console.log(`Moving to previous frame: ${newIndex + 1}/${radarFrames.length}`);
      setCurrentFrameIndex(newIndex);
      setSliderValue(((newIndex) / (radarFrames.length - 1)) * 100);
      updateRadarFrame(newIndex, activeLayer);
    } else {
      console.log('Already at first frame');
    }
  };
  
  // Go to next frame
  const goNextFrame = () => {
    if (currentFrameIndex < radarFrames.length - 1) {
      const newIndex = currentFrameIndex + 1;
      console.log(`Moving to next frame: ${newIndex + 1}/${radarFrames.length}`);
      setCurrentFrameIndex(newIndex);
      setSliderValue(((newIndex) / (radarFrames.length - 1)) * 100);
      updateRadarFrame(newIndex, activeLayer);
    } else {
      console.log('Already at last frame');
    }
  };
  
  // Handle slider change
  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setSliderValue(value);

    // Calculate the frame index based on slider value
    const frameIndex = Math.round((value / 100) * (radarFrames.length - 1));
    
    // Calculate the actual percentage for the gradient based on frameIndex
    const percentage = (frameIndex / (radarFrames.length - 1)) * 100;
    
    // Update the gradient to match the actual frame position
    e.target.style.background = `linear-gradient(to right, #fff 0%, #fff ${percentage}%, rgba(255, 255, 255, 0.2) ${percentage}%, rgba(255, 255, 255, 0.2) 100%)`;
    
    console.log(`Slider moved to ${value}%, showing frame ${frameIndex + 1}/${radarFrames.length}`);
    setCurrentFrameIndex(frameIndex);
    updateRadarFrame(frameIndex, activeLayer);
  };
  
  // --- Extracted Animation Loop Logic ---
  const runAnimationInterval = () => {
    console.log('Starting/Resuming animation interval loop.');
    // Clear any existing interval first
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
    }

    let currentIndex = currentFrameIndex; // Start from the current frame
    animationIntervalRef.current = setInterval(() => {
      if (!frameLayersRef.current || frameLayersRef.current.length === 0) {
        clearInterval(animationIntervalRef.current);
        return;
      }
      const layers = frameLayersRef.current;
      const nextIndex = (currentIndex + 1) % layers.length;

      if (layers[currentIndex] && layers[nextIndex]) {
          layers[currentIndex].setOpacity(0);
          layers[nextIndex].setOpacity(layerOpacity);
      }

      currentIndex = nextIndex;
      setCurrentFrameIndex(currentIndex);
      setSliderValue(Math.round((currentIndex / (layers.length - 1)) * 100));
    }, 600); // Animation frame interval
  };

  // --- Handle Map Zoom End ---
  // Use refs inside, remove state dependencies from useCallback array
  const handleZoomEnd = useCallback(() => {
    const map = mapInstanceRef.current;
    // Read current state from refs
    const playing = isPlayingRef.current;
    const loading = isLoadingAnimationRef.current;

    if (!map || !playing || loading) {
      // Don't handle if map doesn't exist, not playing, or already loading/delaying
      return;
    }

    const currentZoom = map.getZoom();
    const previousZoom = previousZoomRef.current;

    // Check for zoom out while playing
    if (previousZoom !== null && currentZoom < previousZoom) {
      console.log(`Zoom out detected during animation (from ${previousZoom} to ${currentZoom}). Pausing and showing loading.`);

      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      if (zoomDelayTimeoutRef.current) {
        clearTimeout(zoomDelayTimeoutRef.current);
         zoomDelayTimeoutRef.current = null; // Clear ref immediately
      }

      // Set loading state *before* starting the timeout
      // Use the ref to check if we need to set state
      if (!isLoadingAnimationRef.current) setIsLoadingAnimation(true);

      const ZOOM_DELAY_MS = 3000;
      zoomDelayTimeoutRef.current = setTimeout(() => {
        // Read ref again inside timeout
        if (isPlayingRef.current) {
          console.log('Resuming animation after zoom delay.');
          // Set state only when necessary
          // Check ref before setting state to avoid unnecessary re-renders
          if (isLoadingAnimationRef.current) setIsLoadingAnimation(false);
          runAnimationInterval();
        } else {
          console.log('Animation was stopped during zoom delay.');
          if (isLoadingAnimationRef.current) setIsLoadingAnimation(false);
        }
        zoomDelayTimeoutRef.current = null; // Clear ref after execution
      }, ZOOM_DELAY_MS);

    }

    previousZoomRef.current = currentZoom;
  // Remove dependencies: isPlaying, isLoadingAnimation, currentFrameIndex
  }, []); // <-- Empty dependency array makes handleZoomEnd stable 

   // --- Initialize Map ---

  // Function to start radar animation
  const startRadarAnimation = () => {
    if (!radarFrames || radarFrames.length === 0) return;
    
    stopRadarAnimation(true); // Clear previous state but indicate we are restarting
    console.log('Starting radar animation preparation...');
    setIsLoadingAnimation(true); // Set loading state
    
    // Ensure the single static frame is removed if present
    if (weatherLayerRef.current && mapInstanceRef.current) {
      console.log('Removing initial/paused weather layer before starting animation');
      mapInstanceRef.current.removeLayer(weatherLayerRef.current);
      weatherLayerRef.current = null;
    }
    
    console.log('Creating frame layers');
    frameLayersRef.current = radarFrames.map((frame, index) => {
      const layer = window.L.tileLayer(frame.url, {
        opacity: index === currentFrameIndex ? layerOpacity : 0, // Show first frame initially
        zIndex: 10,
        minZoom: 3,
        maxZoom: 11,
        maxNativeZoom: 11
      });
      // Add layers to map immediately so Leaflet can start loading tiles
      if (mapInstanceRef.current) {
         layer.addTo(mapInstanceRef.current);
      } else {
         console.warn('Map instance not available when creating frame layers');
      }
      return layer;
    });
    
    // Set the initial frame time
    if (radarFrames && radarFrames[currentFrameIndex]) {
        setCurrentFrameTime(radarFrames[currentFrameIndex].timeLabel || '');
    }
    
    // Delay starting the interval loop
    console.log('Starting 3-second delay before animation loop begins...');
    animationStartTimeoutRef.current = setTimeout(() => {
        // Directly check the current isPlaying state when the timeout fires
        if (isPlaying) { 
            console.log('Initial delay complete. Starting animation interval loop.');
            setIsLoadingAnimation(false); // Clear loading state
            runAnimationInterval(); // Start the loop
        } else {
             console.log('Animation was stopped during initial loading delay.');
             // Ensure loading state is off if we were stopped during the delay
             setIsLoadingAnimation(false); 
        }
        animationStartTimeoutRef.current = null; // Clear the ref after execution
    }, 6000); // 6-second delay before starting loop
  };
  
  // Function to stop radar animation
  const stopRadarAnimation = (isRestarting = false) => {
    // Clear the start timeout if it's pending
    if (animationStartTimeoutRef.current) {
        console.log('Clearing pending animation start timeout.');
        clearTimeout(animationStartTimeoutRef.current);
        animationStartTimeoutRef.current = null;
    }
    // Clear the zoom delay timeout if it's pending
    if (zoomDelayTimeoutRef.current) {
        console.log('Clearing pending zoom delay timeout.');
        clearTimeout(zoomDelayTimeoutRef.current);
        zoomDelayTimeoutRef.current = null;
    }
    
    // Clear the main animation interval
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    
    // Clean up frame layers
    if (frameLayersRef.current.length > 0) {
      console.log('Stopping animation: removing frame layers');
      frameLayersRef.current.forEach(layer => {
        if (mapInstanceRef.current && mapInstanceRef.current.hasLayer(layer)) {
          mapInstanceRef.current.removeLayer(layer);
        }
      });
      frameLayersRef.current = [];
    }
    
    // Clear loading state if we are stopping (not just restarting internally)
    if (!isRestarting) {
        setIsLoadingAnimation(false);
        
        // Re-display the current single frame only if truly stopping
        if (mapInstanceRef.current && radarFrames.length > 0 && currentFrameIndex < radarFrames.length) {
            console.log(`Stopping animation: displaying frame ${currentFrameIndex}`);
            const currentFrame = radarFrames[currentFrameIndex];
            if (currentFrame) {
                if (weatherLayerRef.current) {
                  mapInstanceRef.current.removeLayer(weatherLayerRef.current);
                  weatherLayerRef.current = null;
                }
                displayRadarFrame(currentFrame, currentFrameIndex); 
            }
        }
        // We don't explicitly set isPlaying to false here, as that's handled by the calling context (useEffect or togglePlayPause)
    }
  };
  
  // Play/pause animation effect (Simplified back)
  useEffect(() => {
    if (isPlaying) {
      // Ensure radar data is loaded before starting animation
      if (radarFrames.length > 0) {
          console.log("isPlaying is true, starting animation.");
          startRadarAnimation();
      } else {
          console.log("isPlaying is true, but no radar frames yet. Waiting for data.");
          // Optionally trigger data load if needed, though initial load should handle it
          // loadRadarData(); 
      }
    } else {
      console.log("isPlaying is false, stopping animation.");
      stopRadarAnimation();
    }
  }, [isPlaying, radarFrames]); // Add radarFrames dependency
  
  // Format time display
  const formatTimeDisplay = () => {
    if (!timestamp) return '';
    
    try {
      // For ISO timestamps
      if (timestamp.includes('T')) {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      }
      
      // For UTC timestamps
      if (timestamp.includes('UTC')) {
        const timeRegex = /(\d+):(\d+)\s+(AM|PM)\s+UTC/;
        const matches = timestamp.match(timeRegex);
        
        if (matches && matches.length >= 4) {
          let hours = parseInt(matches[1]);
          const minutes = parseInt(matches[2]);
          const isPM = matches[3] === 'PM';
          
          // Convert 12-hour format to 24-hour format
          if (isPM && hours < 12) hours += 12;
          if (!isPM && hours === 12) hours = 0;
          
          // Create a date object with today's date and the UTC time
          const now = new Date();
          const utcDate = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate(),
            hours,
            minutes
          ));
          
          // Return the local time
          return utcDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      }
      
      // If it's already in a readable format
      return timestamp;
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestamp;
    }
  };
  
  // Calculate current frame information
  const getCurrentFrameInfo = () => {
    if (!radarFrames.length) return '0/0';
    return `${currentFrameIndex + 1}/${radarFrames.length}`;
  };
  
  
  
  // Stop animation when component unmounts
  useEffect(() => {
    // Return cleanup function
    return () => {
      // Clear animation interval
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
      // Clear initial start timeout
      if (animationStartTimeoutRef.current) {
        clearTimeout(animationStartTimeoutRef.current);
      }
      // Clear zoom delay timeout
      if (zoomDelayTimeoutRef.current) {
        clearTimeout(zoomDelayTimeoutRef.current);
      }
      // Attempt to remove map listener if map instance exists
      const map = mapInstanceRef.current;
      if (map && handleZoomEnd) { // Check if map and handler exist
        try {
           console.log('Removing zoomend listener on unmount.');
           map.off('zoomend', handleZoomEnd);
        } catch (e) {
            console.warn('Could not remove zoomend listener on unmount:', e);
        }
      }
    };
  }, [handleZoomEnd]); // Add handleZoomEnd dependency to ensure correct listener removal
  
  // Effect to reload data when zipcode changes
  useEffect(() => {
    let isSubscribed = true; // Track if effect is still active
    
    if (currentZipcode && mapLoaded && mapInstanceRef.current) {
      console.log(`Map ready, loading radar data for zipcode: ${currentZipcode}`);
      
      // Single timeout for initial load
      const loadTimer = setTimeout(async () => {
        if (isSubscribed) {
          await loadRadarData();
          
          // Only set up retry if we still need it
          if (isSubscribed && radarFrames.length === 0) {
            console.log('No radar frames loaded, will retry once');
            // Single retry after 2 seconds
            const retryTimer = setTimeout(() => {
              if (isSubscribed && radarFrames.length === 0) {
                console.log('Retrying radar data load');
                loadRadarData();
              }
            }, 2000);
            
            return () => clearTimeout(retryTimer);
          }
        }
      }, 100);
      
      return () => {
        isSubscribed = false;
        clearTimeout(loadTimer);
      };
    }
  }, [currentZipcode, mapLoaded]);
  
  // Helper function to format frame timestamp to local time
  const formatFrameTimeLocal = (timeString) => {
    if (!timeString) return '';
    
    try {
        // Handle timestamps like "8:48 AM UTC"
        if (timeString.includes('UTC')) {
            const timeRegex = /(\d{1,2}):(\d{2})\s+(AM|PM)\s+UTC/;
            const matches = timeString.match(timeRegex);

            if (matches && matches.length >= 4) {
                let hours = parseInt(matches[1]);
                const minutes = parseInt(matches[2]);
                const isPM = matches[3] === 'PM';

                // Convert 12-hour format to 24-hour format
                if (isPM && hours < 12) hours += 12;
                if (!isPM && hours === 12) hours = 0; // Midnight case

                // Create a date object using today's date but with the specified UTC time
                const now = new Date();
                const utcDate = new Date(Date.UTC(
                    now.getUTCFullYear(),
                    now.getUTCMonth(),
                    now.getUTCDate(),
                    hours,
                    minutes
                ));

                // Return the time formatted in the user's local timezone
                return utcDate.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                    // timeZoneName: 'short' // Optionally add timezone name
                });
            }
        }
        
        // Handle potential ISO 8601 timestamps (like 2023-10-27T15:48:00Z)
        if (timeString.includes('T') && timeString.endsWith('Z')) {
            const date = new Date(timeString);
            if (!isNaN(date.getTime())) {
                return date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
            }
        }

        // If no specific format matched or conversion failed, return the original string
        return timeString;
    } catch (error) {
        console.error('Error formatting frame time:', error);
        return timeString; // Fallback to original string on error
    }
  };
  
  // Effect to handle radar data loading when map is ready
  useEffect(() => {
    let isSubscribed = true;

    const loadData = async () => {
      if (!mapLoaded || !mapInstanceRef.current || isLoadingRef.current) {
        return;
      }

      try {
        console.log('Map ready, loading initial radar data');
        isLoadingRef.current = true;
        
        // Generate a unique request ID
        const requestId = ++currentRequestIdRef.current;
        
        await loadRadarData();
        
        // Only proceed if this is still the current request
        if (isSubscribed && requestId === currentRequestIdRef.current) {
          if (radarFrames.length === 0) {
            console.log('No radar frames loaded, will retry once');
            
            // Clear any existing retry timer
            if (retryTimerRef.current) {
              clearTimeout(retryTimerRef.current);
            }
            
            retryTimerRef.current = setTimeout(async () => {
              // Only retry if we're still on the same request and still need frames
              if (isSubscribed && requestId === currentRequestIdRef.current && radarFrames.length === 0) {
                console.log('Retrying radar data load');
                try {
                  await loadRadarData();
                } finally {
                  if (isSubscribed) {
                    isLoadingRef.current = false;
                    retryTimerRef.current = null;
                  }
                }
              }
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error loading radar data:', error);
      } finally {
        // Only reset loading state if we're not in a retry
        if (!retryTimerRef.current) {
          isLoadingRef.current = false;
        }
      }
    };

    const loadTimer = setTimeout(loadData, 100);
    
    return () => {
      isSubscribed = false;
      clearTimeout(loadTimer);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      isLoadingRef.current = false;
      // Increment request ID to cancel any pending operations
      currentRequestIdRef.current++;
    };
  }, [mapLoaded]);

  // Separate effect to handle map location updates when zipcode changes
  useEffect(() => {
    if (!mapInstanceRef.current || !currentZipcode) {
       console.log('Map location update effect: Skipping (no map or zipcode)');
       return;
    }

    const updateMapLocation = async () => {
      console.log(`Map location update effect: Triggered for ${currentZipcode}`);
      try {
        // Use the refactored function to get coordinates
        // Force lookup might be needed if radar data doesn't consistently update cache
        const forceLookup = true; // Set to true to always fetch coords on zip change
        const coords = await getCoordinatesForZipcode(currentZipcode, forceLookup);

        if (coords && mapInstanceRef.current) {
          console.log(`Map location update effect: Setting view for ${currentZipcode}: ${coords.lat}, ${coords.lng}`);
          mapInstanceRef.current.setView([coords.lat, coords.lng], 8); // Keep zoom at 8, or adjust as needed
        } else {
          console.warn(`Map location update effect: Could not get coordinates for ${currentZipcode}`);
        }

        // We still need to load the radar data for the new location
        console.log(`Map location update effect: Triggering radar data load for ${currentZipcode}`);
        loadRadarData(); // This will fetch new radar data which includes lat/lng

      } catch (error) {
        console.error('Error updating map location:', error);
      }
    };

    // Always run the update when currentZipcode changes 
    // (The getCoordinates function handles caching internally)
    updateMapLocation();

  }, [currentZipcode]); // Run only when currentZipcode changes
  
  // Update frame time whenever the index or frames change
  useEffect(() => {
    if (radarFrames && radarFrames.length > currentFrameIndex && currentFrameIndex >= 0) {
      const newTimeLabel = radarFrames[currentFrameIndex].timeLabel || '';
      // Only update if the label is actually different to avoid potential loops
      if (newTimeLabel !== currentFrameTime) { 
        setCurrentFrameTime(newTimeLabel);
      }
    } else {
      // Reset time if frames are cleared or index is invalid
       if (currentFrameTime !== '') { 
         setCurrentFrameTime(''); 
       }
    }
  }, [currentFrameIndex, radarFrames]); // Rerun when index or frames change
  
  return (
    <div className="tab-content map-tab">
      <div className="weather-map" ref={mapContainerRef}>
        {!mapLoaded && <div className="map-loading">Weather Map Loading...</div>}
        {radarLoading && <div className="radar-loading">Loading Radar Data...</div>}
        {/* Add the new animation loading message */}
        {isLoadingAnimation && (
          <div className="map-loading animation-loading">Loading animation...</div>
        )}
        {radarError && !radarLoading && (
          <div className="radar-error">
            <div className="error-message">{radarError}</div>
            <button className="retry-btn" onClick={() => loadRadarData()}>Retry</button>
          </div>
        )}
        
        {/* Map Layer Toolbar with layer name and color key */}
        <MapLayerToolbar 
          activeLayer={activeLayer} 
          productParam={currentProduct} 
        />
        
        {/* Layers Panel */}
        <MapLayers 
          isOpen={showLayersPanel}
          onClose={() => setShowLayersPanel(false)}
          onLayerSelect={handleLayerSelect}
          activeLayer={activeLayer} // This is for the main weather layer
          activeBaseLayer={activeBaseLayer} // Pass down the active base layer
          zipcode={currentZipcode}
        />
      </div>
      <div className="radar-controls bottom-bar">
        <div className="timeline-controls">
          <button className="control-btn prev-btn" onClick={goPrevFrame}>
            <svg className="icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>
          <button 
            className="control-btn play-btn" 
            onClick={togglePlayPause}
            disabled={isLoadingAnimation} // Disable button while loading animation
          >
            {isPlaying ? (
              <svg className="icon" viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            ) : (
              <svg className="icon" viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          <button className="control-btn next-btn" onClick={goNextFrame} disabled={isLoadingAnimation}>
            <svg className="icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>
        </div>
        
        <div className="timeline-container">
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={sliderValue}
            onChange={handleSliderChange}
            className="time-slider" 
            disabled={isLoadingAnimation} // Disable slider while loading animation
            style={{
              background: `linear-gradient(to right, #fff ${sliderValue}%, rgba(255, 255, 255, 0.2) ${sliderValue}%)`
            }}
          />
          <div className="time-display">
            <span className="frame-info">{getCurrentFrameInfo()}</span>
            <span className="timestamp">{formatFrameTimeLocal(currentFrameTime) || formatTimeDisplay()}</span>
          </div>
        </div>
        
        <div className="control-right">
          <button className="control-btn layers-btn" onClick={toggleLayersPanel}>
            <svg className="icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>
            </svg>
            <span className="layers-btn-text">LAYERS</span>
          </button>
        </div>
      </div>
    </div>
  );
});

export default WeatherMap; 